// Fullscreen photo viewer for booth detail. Dependency-free (vanilla), consistent
// with the rest of the app. OFFLINE-SAFE: it reuses the object/blob URLs already
// loaded into the thumbnails — no new fetches, no network — so it works in
// airplane mode on already-loaded photos. It never revokes those URLs on close
// (the thumbnails still reference them).
//
// Interaction: with >1 image, swipe left/right (touch) or ←/→ (keyboard) pages
// through, and a "n / N" counter shows position. Close via the ✕ button, a tap on
// the backdrop, a swipe-down, or Esc. A single photo just opens fullscreen.
//
// v0.6 zoom (hand-rolled, still zero-dep): pinch to zoom (1×–4×), double-tap
// toggles fit ↔ 2.5× centred on the tapped point, one-finger drag pans while
// zoomed. THE HAND-OFF: paging and swipe-down-to-close are active ONLY at fit —
// while zoomed a one-finger drag pans and must never page or close. Paging resets
// to fit, so every photo opens un-zoomed.
//
// v1.1 per-photo notes: the viewer is the PRIMARY surface for a photo's own note.
// The image is aligned to the TOP of a `stage` element and the note sits in its own
// section BELOW it — never overlaid (Brandon's call: reading a price over a busy
// product photo is exactly the problem this feature exists to fix). Because the
// image now lives in the stage rather than filling the overlay, all the zoom/pan
// maths is measured against the STAGE — using the overlay would centre the photo
// against the note panel's height too and let a pan drift under it.
//
// Callers pass photo identity (`localId`) alongside the URL so an edit can be
// written back to the right PhotoRef; `onNoteSave` does the persisting (each screen
// owns its own storage path). Notes flush on blur, on paging, and on close.

const SWIPE_THRESHOLD = 45; // px — deliberate, avoids firing on taps/jitter
const MIN_SCALE = 1; // "fit" — the object-fit:contain size
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300; // max gap between the two taps
const DOUBLE_TAP_SLOP = 30; // px — the two taps must land in roughly one spot

export interface LightboxPhoto {
  /** Object/blob URL already loaded in the thumbnail (never re-fetched here). */
  url: string;
  /** PhotoRef.local_id — the identity a note is written back against. */
  localId: string;
  /** Current note, if any. */
  note?: string;
}

export interface LightboxOptions {
  /**
   * Persist an edited note. Called with the trimmed text ('' = cleared) when the
   * field flushes (blur / page change / close) and the value actually changed.
   * Errors are the caller's to surface; a rejection is logged and swallowed here.
   */
  onNoteSave?: (localId: string, note: string) => void | Promise<void>;
}

export function openLightbox(
  photos: LightboxPhoto[],
  startIndex: number,
  opts: LightboxOptions = {},
): void {
  if (!photos.length) return;
  const multi = photos.length > 1;
  let i = Math.max(0, Math.min(startIndex, photos.length - 1));

  const overlay = document.createElement('div');
  overlay.className = 'lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML = `
    <button class="lightbox__close" type="button" aria-label="Close">✕</button>
    ${multi ? '<div class="lightbox__count" aria-live="polite"></div>' : ''}
    <div class="lightbox__stage">
      <img class="lightbox__img" alt="booth photo" draggable="false" />
    </div>
    <div class="lightbox__note">
      <label class="lightbox__note-label" for="lightbox-note">Photo note</label>
      <textarea id="lightbox-note" class="lightbox__note-input" rows="2"
                placeholder="Price, pack, colours&hellip;"
                autocapitalize="sentences" autocomplete="off" spellcheck="false"></textarea>
    </div>
  `;
  document.body.appendChild(overlay);

  // Lock background scroll while the viewer is up.
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const stage = overlay.querySelector<HTMLElement>('.lightbox__stage')!;
  const img = overlay.querySelector<HTMLImageElement>('.lightbox__img')!;
  const countEl = overlay.querySelector<HTMLElement>('.lightbox__count');
  const notePanel = overlay.querySelector<HTMLElement>('.lightbox__note')!;
  const noteInput = overlay.querySelector<HTMLTextAreaElement>('.lightbox__note-input')!;

  // ---- zoom/pan state -------------------------------------------------------
  // Rendered as `translate(tx, ty) scale(scale)` with transform-origin center, so
  // an image point `u` (px from the image's own centre, at fit size) sits at
  // screen `centre + t + u * scale`.
  let scale = MIN_SCALE;
  let tx = 0;
  let ty = 0;

  function applyTransform(): void {
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    img.classList.toggle('lightbox__img--zoomed', scale > MIN_SCALE);
  }

  function resetZoom(): void {
    scale = MIN_SCALE;
    tx = 0;
    ty = 0;
    applyTransform();
  }

  /** Keep the (scaled) image covering the stage centre: no dragging it off into
   *  empty space. At fit there's no slack, so both axes clamp to 0. */
  function clampPan(): void {
    const slackX = Math.max(0, (img.clientWidth * scale - stage.clientWidth) / 2);
    const slackY = Math.max(0, (img.clientHeight * scale - stage.clientHeight) / 2);
    tx = Math.max(-slackX, Math.min(slackX, tx));
    ty = Math.max(-slackY, Math.min(slackY, ty));
  }

  /** Zoom to `next`, holding the image point currently under `(px, py)` in place. */
  function zoomTo(next: number, px: number, py: number): void {
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
    const r = stage.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const ux = (px - cx - tx) / scale;
    const uy = (py - cy - ty) / scale;
    scale = clamped;
    tx = px - cx - ux * scale;
    ty = py - cy - uy * scale;
    if (scale === MIN_SCALE) {
      tx = 0;
      ty = 0;
    }
    clampPan();
    applyTransform();
  }

  function toggleZoomAt(px: number, py: number): void {
    if (scale > MIN_SCALE) resetZoom();
    else zoomTo(DOUBLE_TAP_SCALE, px, py);
  }

  // ---- notes ----------------------------------------------------------------
  // The field edits the photo we're ON. Any move away from that photo (paging or
  // close) flushes first, so a note typed and swiped away from is never lost.
  let noteOwner = i; // index the textarea's current text belongs to

  function flushNote(): void {
    const owner = photos[noteOwner];
    if (!owner) return;
    const next = noteInput.value.trim();
    if (next === (owner.note ?? '')) return;
    owner.note = next;
    try {
      const r = opts.onNoteSave?.(owner.localId, next);
      if (r && typeof (r as Promise<void>).catch === 'function') {
        void (r as Promise<void>).catch((err) => console.error('photo note save failed', err));
      }
    } catch (err) {
      console.error('photo note save failed', err);
    }
  }

  noteInput.addEventListener('blur', flushNote);

  // iOS does NOT scroll a position:fixed overlay clear of the on-screen keyboard,
  // so the note band would sit under it. Shrink the overlay to the visual viewport
  // instead: the stage gives up the height and the field stays visible while typing.
  const vv = window.visualViewport;
  const fitKeyboard = (): void => {
    if (!vv) return;
    const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    overlay.style.bottom = inset ? `${inset}px` : '';
  };
  vv?.addEventListener('resize', fitKeyboard);
  vv?.addEventListener('scroll', fitKeyboard);

  // ---- paging / lifecycle ---------------------------------------------------
  function show(): void {
    img.src = photos[i].url;
    resetZoom(); // every photo opens at fit
    if (countEl) countEl.textContent = `${i + 1} / ${photos.length}`;
    noteOwner = i;
    noteInput.value = photos[i].note ?? '';
  }

  function go(delta: number): void {
    if (!multi) return;
    flushNote(); // the note belongs to the photo we're leaving
    noteInput.blur();
    i = (i + delta + photos.length) % photos.length;
    show();
  }

  function close(): void {
    flushNote();
    document.body.style.overflow = prevOverflow;
    window.removeEventListener('keydown', onKey);
    vv?.removeEventListener('resize', fitKeyboard);
    vv?.removeEventListener('scroll', fitKeyboard);
    overlay.remove();
    // NB: do NOT revoke the URLs — the thumbnails still use them.
  }

  /** True while the user is interacting with the note field rather than the photo. */
  function inNote(target: EventTarget | null): boolean {
    return target instanceof Node && notePanel.contains(target);
  }

  function onKey(e: KeyboardEvent): void {
    // Typing a note must not page the photo (← / → are cursor keys in a textarea)
    // or dismiss the viewer mid-sentence; Esc steps out of the field first.
    if (document.activeElement === noteInput) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        noteInput.blur();
      }
      return;
    }
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight') go(1);
    else if (e.key === 'ArrowLeft') go(-1);
  }
  window.addEventListener('keydown', onKey);

  overlay.querySelector('.lightbox__close')!.addEventListener('click', close);
  // Backdrop tap closes — but only at fit; while zoomed the backdrop is just the
  // area around a panned photo and a stray tap there shouldn't dismiss it. The
  // note section is NOT backdrop: tapping in there is how you start typing.
  overlay.addEventListener('click', (e) => {
    if ((e.target === overlay || e.target === stage) && scale === MIN_SCALE) close();
  });
  // Desktop parity for double-tap.
  img.addEventListener('dblclick', (e) => {
    e.preventDefault();
    toggleZoomAt(e.clientX, e.clientY);
  });

  // ---- touch ----------------------------------------------------------------
  // One-finger: at fit → swipe to page / swipe down to close; zoomed → pan.
  // Two-finger: pinch to zoom (with two-finger pan), anytime.
  // Touches inside the note section are left entirely alone (the overlay sets
  // touch-action:none for the gesture surface; the note panel opts back in) so
  // the field can scroll, select and place a caret normally.
  let sx = 0; // gesture start, for the swipe decision
  let sy = 0;
  let lastX = 0; // previous move position, for incremental panning
  let lastY = 0;
  let tracking = false; // a live one-finger gesture
  let panning = false; // ...that is panning rather than swiping
  let pinching = false;
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let multiTouched = false; // this gesture involved 2+ fingers at some point
  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;

  const dist = (t: TouchList): number =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  overlay.addEventListener(
    'touchstart',
    (e) => {
      if (inNote(e.target)) {
        tracking = false;
        pinching = false;
        return;
      }
      if (e.touches.length === 2) {
        tracking = false;
        panning = false;
        pinching = true;
        multiTouched = true;
        pinchStartDist = dist(e.touches);
        pinchStartScale = scale;
        lastX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        lastY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        e.preventDefault(); // don't let Safari page-zoom instead
      } else if (e.touches.length === 1) {
        tracking = true;
        panning = scale > MIN_SCALE;
        pinching = false;
        multiTouched = false;
        sx = lastX = e.touches[0].clientX;
        sy = lastY = e.touches[0].clientY;
      } else {
        tracking = false;
        multiTouched = true;
      }
    },
    { passive: false },
  );

  overlay.addEventListener(
    'touchmove',
    (e) => {
      if (inNote(e.target)) return;
      if (pinching && e.touches.length === 2) {
        const d = dist(e.touches);
        if (pinchStartDist > 0) {
          const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          // Pan by the midpoint drift first, then zoom about the current midpoint —
          // together that's the standard "the photo follows your fingers" feel.
          tx += mx - lastX;
          ty += my - lastY;
          lastX = mx;
          lastY = my;
          zoomTo(pinchStartScale * (d / pinchStartDist), mx, my);
        }
        e.preventDefault();
        return;
      }
      if (tracking && panning && e.touches.length === 1) {
        tx += e.touches[0].clientX - lastX;
        ty += e.touches[0].clientY - lastY;
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
        clampPan();
        applyTransform();
        e.preventDefault(); // a zoomed drag pans — never pages, never closes
      }
    },
    { passive: false },
  );

  overlay.addEventListener(
    'touchend',
    (e) => {
      if (inNote(e.target)) return;
      if (pinching) {
        if (e.touches.length === 0) {
          pinching = false;
          if (scale <= MIN_SCALE) resetZoom(); // settle exactly at fit
        }
        return;
      }
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      // A tap (either zoom level): second one within the window toggles zoom.
      if (!multiTouched && absX < DOUBLE_TAP_SLOP && absY < DOUBLE_TAP_SLOP) {
        const now = e.timeStamp;
        if (
          now - lastTapAt < DOUBLE_TAP_MS &&
          Math.abs(t.clientX - lastTapX) < DOUBLE_TAP_SLOP &&
          Math.abs(t.clientY - lastTapY) < DOUBLE_TAP_SLOP
        ) {
          lastTapAt = 0;
          e.preventDefault(); // suppress the synthesized click (backdrop-close)
          toggleZoomAt(t.clientX, t.clientY);
        } else {
          lastTapAt = now;
          lastTapX = t.clientX;
          lastTapY = t.clientY;
        }
        panning = false;
        return;
      }

      // Swipes only at fit. While zoomed the drag was a pan and is already applied.
      if (!panning && scale === MIN_SCALE && !multiTouched) {
        if (absX > absY && absX > SWIPE_THRESHOLD) {
          go(dx < 0 ? 1 : -1); // swipe left → next
        } else if (dy > SWIPE_THRESHOLD && absY > absX) {
          close(); // swipe down
        }
      }
      panning = false;
    },
    { passive: false },
  );

  overlay.addEventListener('touchcancel', () => {
    tracking = false;
    panning = false;
    pinching = false;
  });

  show();
}
